import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import ChangePasswordModal from "./ChangePasswordModal";
import { LayoutDashboard, Menu, X, KeyRound, LogOut, Building, Shield } from "lucide-react";

function roleLabel(user) {
  if (!user) return "";
  if (user.is_platform_owner) return "PLATFORM OWNER";
  if (user.is_org_admin) return "ORG ADMIN";
  if (user.is_authorized) return "MANAGER";
  return "PENDING";
}

/**
 * App header.
 *
 * Desktop (≥md): a horizontal nav bar showing greeting, Dashboard/Org/Platform
 * shortcuts, and Sign out.
 *
 * Mobile: a compact bar with only the logo + a hamburger. The hamburger
 * opens a full-width drawer with every nav action stacked vertically so
 * nothing is clipped at 390px. Auto-closes on route change (via <Link>) and
 * on Escape/backdrop click.
 */
export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const drawerRef = useRef(null);

  // Close on Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const doSignOut = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-40 border-b border-[#111111] bg-[#F4F4F0]/90 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-10 h-14 md:h-16 flex items-center justify-between gap-3">
        <Link
          to={user ? "/dashboard" : "/"}
          className="flex items-center gap-2 md:gap-3 min-w-0"
          data-testid="logo-link"
          onClick={() => setMenuOpen(false)}
        >
          <div className="h-6 w-6 md:h-7 md:w-7 border border-[#111] flex items-center justify-center flex-none">
            <div className="h-2.5 w-2.5 md:h-3 md:w-3 bg-[#FF4F00]" />
          </div>
          <span className="font-display font-bold text-lg md:text-xl tracking-tight">CALIBRATE</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <button
                onClick={() => setPwOpen(true)}
                className="hidden lg:inline-flex font-mono-cal text-[11px] uppercase tracking-widest text-[#666] hover:text-[#FF4F00] underline-offset-4 hover:underline max-w-[260px] truncate"
                title="Click to change your password"
                data-testid="user-greeting"
              >
                [{user.email}] [{roleLabel(user)}]
              </button>
              {user.is_authorized && (
                <Link
                  to="/dashboard"
                  className="cal-btn cal-btn-ghost"
                  title="Back to Dashboard"
                  data-testid="nav-dashboard"
                >
                  <LayoutDashboard size={14} />
                  <span className="hidden lg:inline ml-1">Dashboard</span>
                </Link>
              )}
              {user.is_org_admin && (
                <Link to="/admin/org" className="cal-btn cal-btn-ghost" data-testid="nav-org-admin">Org</Link>
              )}
              {user.is_platform_owner && (
                <Link to="/admin/platform" className="cal-btn cal-btn-ghost" data-testid="nav-platform-admin">Platform</Link>
              )}
              <button onClick={doSignOut} className="cal-btn cal-btn-ghost" data-testid="logout-btn">
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/pricing" className="cal-btn cal-btn-ghost" data-testid="nav-pricing">Pricing</Link>
              <Link to="/login" className="cal-btn cal-btn-ghost" data-testid="nav-login">Sign in</Link>
              <Link to="/register" className="cal-btn" data-testid="nav-register">Get started</Link>
            </>
          )}
        </nav>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center w-10 h-10 border border-[#111] hover:bg-[#111] hover:text-white transition"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          aria-expanded={menuOpen}
          data-testid="header-menu-toggle"
        >
          {menuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          className="md:hidden fixed inset-0 top-14 z-40 bg-black/40"
          onClick={(e) => { if (e.target === e.currentTarget) setMenuOpen(false); }}
          data-testid="header-menu-backdrop"
        >
          <div
            ref={drawerRef}
            className="bg-[#F4F4F0] border-b border-[#111] shadow-lg"
            data-testid="header-menu-drawer"
          >
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-2">
              {user ? (
                <>
                  <div className="pb-3 border-b border-[#111]/20 mb-1">
                    <div className="font-display font-semibold text-base truncate">{user.email}</div>
                    <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mt-0.5">
                      {roleLabel(user)}
                    </div>
                  </div>
                  {user.is_authorized && (
                    <Link
                      to="/dashboard"
                      onClick={() => setMenuOpen(false)}
                      className="cal-btn cal-btn-ghost justify-start"
                      data-testid="mobile-nav-dashboard"
                    >
                      <LayoutDashboard size={14} /> Dashboard
                    </Link>
                  )}
                  {user.is_org_admin && (
                    <Link
                      to="/admin/org"
                      onClick={() => setMenuOpen(false)}
                      className="cal-btn cal-btn-ghost justify-start"
                      data-testid="mobile-nav-org-admin"
                    >
                      <Building size={14} /> Organization
                    </Link>
                  )}
                  {user.is_platform_owner && (
                    <Link
                      to="/admin/platform"
                      onClick={() => setMenuOpen(false)}
                      className="cal-btn cal-btn-ghost justify-start"
                      data-testid="mobile-nav-platform-admin"
                    >
                      <Shield size={14} /> Platform admin
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); setPwOpen(true); }}
                    className="cal-btn cal-btn-ghost justify-start"
                    data-testid="mobile-nav-change-password"
                  >
                    <KeyRound size={14} /> Change password
                  </button>
                  <button
                    type="button"
                    onClick={doSignOut}
                    className="cal-btn cal-btn-ghost justify-start text-[#B00020]"
                    data-testid="mobile-nav-logout"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/pricing" onClick={() => setMenuOpen(false)} className="cal-btn cal-btn-ghost justify-start">Pricing</Link>
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="cal-btn cal-btn-ghost justify-start">Sign in</Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="cal-btn justify-start">Get started</Link>
                </>
              )}
            </nav>
          </div>
        </div>
      )}

      {user && (
        <ChangePasswordModal
          open={pwOpen}
          onClose={() => setPwOpen(false)}
          email={user.email}
        />
      )}
    </header>
  );
}
